// src/storage/readinessEngine.ts — Bexley 11+ SAS Calculation & Readiness Engine (FR-DASH-001, FR-DASH-002)
// Calibrated to Quest Assessments (Atom Learning) for Tudor's Jan 2018 cohort.
// Blends verified baselines (Maths 112, SPAG 106, NVR 90%/118) with live practice telemetry.

import { SprintStorage } from '@storage/sprintStorage';
import { ComprehensionStorage } from '@storage/comprehensionStorage';

export type ReadinessBand = 'ahead' | 'on_track' | 'support_needed';

export interface ReadinessThresholds {
  aheadSas: number; // default: 115
  onTrackSas: number; // default: 105
}

export const DEFAULT_THRESHOLDS: ReadinessThresholds = {
  aheadSas: 115,
  onTrackSas: 105,
};

export interface DomainReadiness {
  id: 'numerical' | 'verbal' | 'reading' | 'non_verbal';
  name: string;
  baselineSas: number;
  practiceAccuracy: number | null; // 0.0 to 1.0 or null if no attempts
  estimatedSas: number;
  band: ReadinessBand;
  totalAttempts: number;
  recommendation: string;
}

export interface BexleyReadinessReport {
  domains: Record<string, DomainReadiness>;
  paper1Sas: number; // Verbal + Reading
  paper2Sas: number; // Numerical + NVR
  aggregateSas: number; // Paper 1 + Paper 2 (historical Bexley qualifying threshold ~216)
  qualifyingThreshold: number; // 216
  isQualifyingProjected: boolean;
  thresholds: ReadinessThresholds;
  lastCalculated: number;
}

const CONFIG_STORAGE_KEY = 'clawey_readiness_config_v1';

/** Standard 11+ Age-Standardised Score bounds */
export const MIN_SAS = 69;
export const MAX_SAS = 141;

/** Tudor's verified academic baseline scores (FR-DASH-001) */
export const TUDOR_BASELINES = {
  numerical: 112, // Maths 112 ASS
  verbal: 106, // SPAG 106
  reading: 108, // KS2 Reading Inference/Retrieval baseline
  non_verbal: 118, // NVR 90% accuracy equates to ~118 ASS in Year 4 norm
};

/** Classify SAS into Readiness Band */
export function classifyBand(sas: number, thresholds: ReadinessThresholds = DEFAULT_THRESHOLDS): ReadinessBand {
  if (sas >= thresholds.aheadSas) {
    return 'ahead';
  }
  if (sas >= thresholds.onTrackSas) {
    return 'on_track';
  }
  return 'support_needed';
}

/** Compute estimated SAS from practice accuracy and baseline */
export function calculateDomainSas(
  baselineSas: number,
  accuracy: number | null,
  attemptCount: number = 0
): number {
  if (accuracy === null || attemptCount < 3) {
    return baselineSas;
  }

  // Linear translation: 0% -> 70, 50% -> 100, 75% -> 115, 100% -> 130
  const practiceSas = 70 + accuracy * 60;

  // Weight practice higher as attempt count grows (up to 70% practice, 30% baseline)
  const practiceWeight = Math.min(0.7, 0.3 + attemptCount * 0.04);
  const baselineWeight = 1 - practiceWeight;

  const blended = baselineWeight * baselineSas + practiceWeight * practiceSas;
  return Math.round(Math.max(MIN_SAS, Math.min(MAX_SAS, blended)));
}

export class ReadinessEngine {
  /** Load parent-configured readiness thresholds from local storage */
  static loadThresholds(): ReadinessThresholds {
    try {
      const raw = localStorage.getItem(CONFIG_STORAGE_KEY);
      if (!raw) return { ...DEFAULT_THRESHOLDS };
      const parsed = JSON.parse(raw) as Partial<ReadinessThresholds>;
      return {
        aheadSas: typeof parsed.aheadSas === 'number' ? parsed.aheadSas : DEFAULT_THRESHOLDS.aheadSas,
        onTrackSas: typeof parsed.onTrackSas === 'number' ? parsed.onTrackSas : DEFAULT_THRESHOLDS.onTrackSas,
      };
    } catch {
      return { ...DEFAULT_THRESHOLDS };
    }
  }

  /** Save customized thresholds */
  static saveThresholds(thresholds: ReadinessThresholds): void {
    try {
      localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(thresholds));
    } catch (err) {
      console.warn('Failed to save readiness config:', err);
    }
  }

  /** Compute complete Bexley readiness report from local-first storage telemetry */
  static calculateReport(customThresholds?: ReadinessThresholds): BexleyReadinessReport {
    const thresholds = customThresholds ?? this.loadThresholds();

    // 1. Gather Sprint Data
    const sprintData = SprintStorage.load();
    const sessions = Array.isArray(sprintData.sessions) ? sprintData.sessions : [];

    const paper1Attempts: Array<{ correct: boolean }> = [];
    const paper2MathsAttempts: Array<{ correct: boolean }> = [];
    const paper2NvrAttempts: Array<{ correct: boolean }> = [];

    for (const s of sessions) {
      const attempts = s.attempts || [];
      if (s.paper === 'paper1') {
        for (const a of attempts) paper1Attempts.push({ correct: a.correct });
      } else if (s.paper === 'paper2_maths') {
        for (const a of attempts) paper2MathsAttempts.push({ correct: a.correct });
      } else if (s.paper === 'paper2_nvr') {
        for (const a of attempts) paper2NvrAttempts.push({ correct: a.correct });
      }
    }

    // Also incorporate registry counts if available
    const registry = sprintData.questionRegistry || {};
    for (const entry of Object.values(registry)) {
      if (sessions.length === 0) {
        const correctCount = entry.timesCorrect || 0;
        const total = entry.timesSeen || 0;
        const incorrectCount = Math.max(0, total - correctCount);
        const target = entry.paper === 'paper1'
          ? paper1Attempts
          : entry.paper === 'paper2_maths'
          ? paper2MathsAttempts
          : paper2NvrAttempts;

        for (let i = 0; i < correctCount; i++) target.push({ correct: true });
        for (let i = 0; i < incorrectCount; i++) target.push({ correct: false });
      }
    }

    // 2. Gather Comprehension Data
    const compData = ComprehensionStorage.load();
    const readingAttempts = [
      ...(compData.clueAttempts || []).map((a) => ({ correct: a.correct })),
      ...(compData.vocabAttempts || []).map((a) => ({ correct: a.correct })),
      ...(compData.retrievalSessions || []).map((s) => ({ correct: s.correctCount === s.totalQuestions })),
    ];

    // 3. Helper to get accuracy
    const getStats = (attempts: Array<{ correct: boolean }>) => {
      if (attempts.length === 0) return { accuracy: null, count: 0 };
      const correct = attempts.filter((a) => a.correct).length;
      return { accuracy: correct / attempts.length, count: attempts.length };
    };

    const numericalStats = getStats(paper2MathsAttempts);
    const verbalStats = getStats(paper1Attempts);
    const readingStats = getStats(readingAttempts);
    const nvrStats = getStats(paper2NvrAttempts);

    // 4. Calculate SAS per Domain
    const numericalSas = calculateDomainSas(TUDOR_BASELINES.numerical, numericalStats.accuracy, numericalStats.count);
    const verbalSas = calculateDomainSas(TUDOR_BASELINES.verbal, verbalStats.accuracy, verbalStats.count);
    const readingSas = calculateDomainSas(TUDOR_BASELINES.reading, readingStats.accuracy, readingStats.count);
    const nvrSas = calculateDomainSas(TUDOR_BASELINES.non_verbal, nvrStats.accuracy, nvrStats.count);

    const domains: Record<string, DomainReadiness> = {
      numerical: {
        id: 'numerical',
        name: 'Numerical Reasoning (Maths)',
        baselineSas: TUDOR_BASELINES.numerical,
        practiceAccuracy: numericalStats.accuracy,
        estimatedSas: numericalSas,
        band: classifyBand(numericalSas, thresholds),
        totalAttempts: numericalStats.count,
        recommendation: numericalSas >= thresholds.aheadSas
          ? 'Strong arithmetic foundation! Keep stretching multi-step word problems.'
          : 'Focus on fraction-decimal conversions and inverse operations.',
      },
      verbal: {
        id: 'verbal',
        name: 'Verbal Ability & SPAG',
        baselineSas: TUDOR_BASELINES.verbal,
        practiceAccuracy: verbalStats.accuracy,
        estimatedSas: verbalSas,
        band: classifyBand(verbalSas, thresholds),
        totalAttempts: verbalStats.count,
        recommendation: verbalSas >= thresholds.aheadSas
          ? 'Exceptional vocabulary deduction! Maintain daily exposure to archaic text.'
          : 'Reinforce word analogies and hidden-word detection in daily sprints.',
      },
      reading: {
        id: 'reading',
        name: 'Reading Comprehension',
        baselineSas: TUDOR_BASELINES.reading,
        practiceAccuracy: readingStats.accuracy,
        estimatedSas: readingSas,
        band: classifyBand(readingSas, thresholds),
        totalAttempts: readingStats.count,
        recommendation: readingSas >= thresholds.aheadSas
          ? 'Superb textual evidence retrieval! Tudor excels at pinpoint citations.'
          : 'Practice differentiating direct facts from subtle authorial inferences.',
      },
      non_verbal: {
        id: 'non_verbal',
        name: 'Non-Verbal & Spatial Reasoning',
        baselineSas: TUDOR_BASELINES.non_verbal,
        practiceAccuracy: nvrStats.accuracy,
        estimatedSas: nvrSas,
        band: classifyBand(nvrSas, thresholds),
        totalAttempts: nvrStats.count,
        recommendation: nvrSas >= thresholds.aheadSas
          ? 'Outstanding spatial visualization! High-speed pattern mastery.'
          : 'Continue angle rotations with the Claw Protractor and mirror line.',
      },
    };

    // 5. Paper 1 & Paper 2 Aggregates (Quest Assessments model)
    const paper1Sas = Math.round((verbalSas + readingSas) / 2);
    const paper2Sas = Math.round((numericalSas + nvrSas) / 2);
    const aggregateSas = paper1Sas + paper2Sas;
    const qualifyingThreshold = 216;

    return {
      domains,
      paper1Sas,
      paper2Sas,
      aggregateSas,
      qualifyingThreshold,
      isQualifyingProjected: aggregateSas >= qualifyingThreshold,
      thresholds,
      lastCalculated: Date.now(),
    };
  }
}
